import i18n from '@/plugins/i18n';
import moment from 'moment-hijri';
import { NajizToaster } from '@moj/najiz-toastify';
// Modules
import { ValueWithDefaultUtilModel } from '@/modules/shared/models/utils';
import { HandledServerErrorsStatuses } from '@/modules/shared/services/static-lookups';
import { DateAndTimeMetaConfigsModel, DateAndTimeMetaModel } from '@/modules/shared/models/date';
import { testIds } from '../services/test-automation';

export const valueWithDefault = (data: ValueWithDefaultUtilModel) => {
  const { value, placeholder, suffix } = {
    placeholder: i18n.t('shared.unAvailable') as string,
    suffix: '',
    ...data,
  } as ValueWithDefaultUtilModel;

  if (value && suffix) {
    return `${value} ${suffix}`;
  }

  return value || placeholder || '';
};

export const deepEqual = (obj1: any, obj2: any) => {
  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return obj1 === obj2;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const isEmptyObject = (obj: any): boolean => {
  if (typeof obj === 'object' && obj !== null) {
    return !Object.keys(obj).length;
  }

  return false;
};

export const removeDateSeparator = (date: string) => date.replaceAll(/[-/.]/g, '');

export const redirectToExternalUrl = (configs: { url: string, target?: string, download?: string }) => {
  const a = document.createElement('a');
  a.href = configs.url;
  a.target = configs.target || '_self';

  if (configs.download) {
    a.setAttribute('download', configs.download);
  }

  a.click();
};

export const getDateTimeMeta = (configs: DateAndTimeMetaConfigsModel): DateAndTimeMetaModel => {
  const date = moment(configs.dateTime, configs.dateFormat);
  const isMorning = date.format('a').toLowerCase() === 'am';

  if (configs.dateFormat) {
    return {
      hDate: date.locale('en').format('iYYYY/iMM/iDD'),
      gDate: date.locale('en').format(configs.gDateFormat || 'YYYY-MM-DD'),
      time: date.locale('en').format('h:mm'),
      timeShortSuffix: date.locale('ar').format('a'),
      timeFullSuffix: i18n.t(isMorning ? 'shared.morningTimeFullSuffix' : 'shared.eveningTimeFullSuffix') as string,
      originalTime: date.locale('en').format(configs.timeFormat),
      dayName: date.locale('ar').format('dddd'),
    };
  }

  return {
    hDate: '',
    gDate: '',
    time: '',
    timeShortSuffix: '',
    originalTime: '',
    dayName: '',
    timeFullSuffix: '',
  };
};

export const unifiedPreviewDateFormat = 'YYYY/MM/DD';

export const truncateText = (text: string, limit: number) => {
  if (text.length <= limit) {
    return text;
  }

  return `${text.slice(0, limit)} ...`;
};

export const handleErrors = (httpCode: number, errors: string[], fallbackMessage = '') => {
  const configs: any = {
    timeout: 3000,
    position: 'topCenter',
    id: testIds.toaster.error,
    wrapperClass: 'toaster-center-wrapper',
  };

  if (!HandledServerErrorsStatuses.includes(httpCode)) {
    if (errors && Array.isArray(errors) && errors.length) {
      errors.forEach((message: string) => NajizToaster.error({
        message,
        ...configs,
      }));
    } else if (fallbackMessage) {
      NajizToaster.error({
        message: fallbackMessage,
        ...configs,
      });
    }
  }
};

export const serializeQueryParams = (obj :any) => {
  const generatedQuery: string[] = [];

  const genQuery = (object: any, parentName = '') => Object.entries(object).forEach(([key, value]) => {
    const enhancedKey = parentName ? `${parentName}.${key}` : key;

    if (Array.isArray(value) && value.length) {
      const stringifiedValue = JSON.stringify(value);

      generatedQuery.push(`${key}=${encodeURIComponent(stringifiedValue)}`);
    } else if (typeof value === 'object' && value !== null) {
      genQuery(value, enhancedKey);
    } else if (value) {
      generatedQuery.push(`${enhancedKey}=${value}`);
    }
  });

  genQuery(obj);

  return generatedQuery.length ? `?${generatedQuery.join('&')}` : '';
};

export const showQaimIfEnabled = () => window.$config.qaim.config.enabled || false;
